import { useRef, useState } from 'react';
import { Link, useLocation, useSearchParams } from 'react-router';
import { useIsomorphicLayoutEffect } from 'ahooks';
import {
    Flex,
    Grid,
    useStyles,
    Pagination,
    PaginationItem,
} from '@archipro-design/aria';
import type { PaginationItemCounterProps } from '@archipro-design/aria';
import * as S from './PaginatedArticles.style';
import type { PaginatedArticlesProps } from '@modules/articles/type';
import {
    NarrowContent,
    VideoContent,
} from '@modules/articles/component/page-layout/PageLayout.style';
import { trimPaginationURL } from '~/modules/pagination/util/trim-pagination-url';
import ArticleVideoTile from '@modules/articles/component/article-video-tile/ArticleVideoTile';
import {
    IMAGE_WIDTH_GRID_2,
    IMAGE_WIDTH_GRID_4,
} from '@modules/articles/config/constants';
import { useTileHotButtons } from '~/modules/root';

const PaginatedArticlesDesktop: React.FC<PaginatedArticlesProps> = ({
    articles,
    title,
    totalCount,
    totalPage,
    currentPage,
    pageSize,
    hasNextPage,
    hasPreviousPage,
    useFilmsCategoryLayout,
}) => {
    const { css } = useStyles();
    const location = useLocation();
    const [searchParams] = useSearchParams();

    const articleIDs = articles.map((article) => article.ID);
    const { getTileTopActions, hotButtonModal } = useTileHotButtons({
        itemType: 'article',
        itemIDs: articleIDs,
    });

    const anchorRef = useRef<HTMLDivElement>(null);
    const [anchorY, setAnchorY] = useState(0);

    const renderPaginationLink = (page: number) => {
        return trimPaginationURL(page, searchParams, location);
    };

    useIsomorphicLayoutEffect(() => {
        const anchor = anchorRef.current;
        const offsetTop = anchor?.offsetTop ?? 0;
        const topNavHeight = 200; // TODO: hook to define top nav height
        const gap = 16;
        const offsetBefore = topNavHeight + gap;

        setAnchorY(offsetTop - offsetBefore);
    }, [anchorRef]);

    const columns = useFilmsCategoryLayout ? 2 : 4;
    const imageWidth = useFilmsCategoryLayout
        ? IMAGE_WIDTH_GRID_2
        : IMAGE_WIDTH_GRID_4;

    return (
        <div ref={anchorRef} className={css(S.ArticleSection)}>
            <div className={css(S.EmptySpaceAboveTiles)}></div>
            <div
                className={css(
                    useFilmsCategoryLayout ? VideoContent : NarrowContent,
                )}
            >
                <Flex column={true} className={css(S.Container)}>
                    <Grid columns={columns} className={css(S.ArticleGrid)}>
                        {articles.map((article) => {
                            return (
                                <ArticleVideoTile
                                    key={`archizeen_tile_container--${article.ID}`}
                                    article={article}
                                    imageWidth={imageWidth}
                                    disableVideo={!useFilmsCategoryLayout}
                                    getTileTopActions={getTileTopActions}
                                />
                            );
                        })}
                    </Grid>
                    <Flex
                        className={css(S.PaginationWrapper)}
                        hAlign="center"
                        fill={true}
                    >
                        {totalPage > 1 && (
                            <Pagination
                                key={`pagination-articles-${title}-${pageSize}`}
                                prevButton={{}}
                                nextButton={{}}
                                renderItem={(item) => {
                                    const disablePrev =
                                        item.type === 'previous' &&
                                        !hasPreviousPage;
                                    const disableNext =
                                        item.type === 'next' && !hasNextPage;

                                    if (disablePrev || disableNext) {
                                        return PaginationItem.create(
                                            {
                                                content: item,
                                            },
                                            { generateKey: true },
                                        );
                                    }

                                    return PaginationItem.create({
                                        key: `pagination-articles-item-${item.page}-${item.type}`,
                                        as: Link,
                                        prefetch: 'intent',
                                        to: renderPaginationLink(item.page),
                                        content: item,
                                        state: {
                                            scroll: anchorY,
                                        },
                                    });
                                }}
                                defaultPage={currentPage}
                                count={totalPage}
                                counter={{
                                    range: {
                                        from: (currentPage - 1) * pageSize + 1,
                                        to:
                                            currentPage * pageSize > totalCount
                                                ? totalCount
                                                : currentPage * pageSize,
                                    },
                                    total: totalCount,
                                    textRenderer: (
                                        range: PaginationItemCounterProps['range'],
                                        total: PaginationItemCounterProps['total'],
                                    ) =>
                                        `${range.from} - ${range.to} of ${total} Articles`,
                                }}
                                totalCount={totalCount}
                                pageSize={pageSize}
                                divider={null}
                            />
                        )}
                    </Flex>
                </Flex>
            </div>
            {hotButtonModal}
        </div>
    );
};

export default PaginatedArticlesDesktop;
